import React from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/utils";

export function CartDrawer() {
  const {
    items, isOpen, setIsOpen, updateQty, removeItem, lineKey,
    subtotal, shipping, total, freeShippingRemaining, FREE_SHIPPING_THRESHOLD,
  } = useCart();
  const navigate = useNavigate();

  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-obsidian/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            data-testid="cart-backdrop"
          />
          <motion.aside
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-cream shadow-luxe"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-border px-6 py-5">
              <h2 className="flex items-center gap-2 font-serif text-2xl">
                <ShoppingBag size={20} className="text-gold" /> Votre panier
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setIsOpen(false)}
                className="rounded-full p-2 transition-colors hover:bg-secondary"
              >
                <X size={20} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <ShoppingBag size={44} className="text-muted-foreground/40" />
                <p className="font-serif text-xl">Votre panier est vide</p>
                <button
                  onClick={() => { setIsOpen(false); navigate("/canapes"); }}
                  className="rounded-full bg-obsidian px-6 py-3 text-sm font-semibold text-cream transition-colors hover:bg-gold hover:text-obsidian"
                  data-testid="cart-empty-shop-button"
                >
                  Découvrir la collection
                </button>
              </div>
            ) : (
              <>
                <div className="border-b border-border bg-secondary/50 px-6 py-4">
                  {freeShippingRemaining > 0 ? (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Plus que <span className="font-semibold text-gold-dark">{formatPrice(freeShippingRemaining)}</span> pour la livraison offerte
                    </p>
                  ) : (
                    <p className="mb-2 text-xs font-semibold text-emerald-700">🎉 Livraison gant blanc offerte !</p>
                  )}
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
                    <motion.div
                      className="h-full bg-gold-gradient"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.6 }}
                    />
                  </div>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                  <AnimatePresence initial={false}>
                    {items.map((item) => {
                      const key = lineKey(item);
                      return (
                        <motion.div
                          key={key}
                          layout
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                          className="flex gap-4"
                          data-testid={`cart-item-${item.productId}`}
                        >
                          <img src={item.image} alt={item.name} className="h-24 w-24 rounded-xl object-cover" />
                          <div className="flex flex-1 flex-col">
                            <div className="flex justify-between gap-2">
                              <p className="font-serif text-lg leading-tight">{item.name}</p>
                              <button
                                onClick={() => removeItem(key)}
                                className="text-muted-foreground transition-colors hover:text-destructive"
                                data-testid={`cart-remove-${item.productId}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {[item.color, item.size].filter(Boolean).join(" · ")}
                            </p>
                            <div className="mt-auto flex items-center justify-between">
                              <div className="flex items-center gap-3 rounded-full border border-border px-2 py-1">
                                <button onClick={() => updateQty(key, item.quantity - 1)} data-testid={`cart-dec-${item.productId}`}><Minus size={13} /></button>
                                <span className="w-5 text-center text-sm font-medium">{item.quantity}</span>
                                <button onClick={() => updateQty(key, item.quantity + 1)} data-testid={`cart-inc-${item.productId}`}><Plus size={13} /></button>
                              </div>
                              <span className="font-semibold">{formatPrice(item.price * item.quantity)}</span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>

                <div className="space-y-3 border-t border-border px-6 py-5">
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Sous-total</span><span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Livraison</span><span>{shipping === 0 ? "Offerte" : formatPrice(shipping)}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-3 text-lg font-semibold">
                    <span>Total</span><span data-testid="cart-total">{formatPrice(total)}</span>
                  </div>
                  <button
                    data-testid="cart-checkout-button"
                    onClick={() => { setIsOpen(false); navigate("/commande"); }}
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-obsidian py-4 text-sm font-semibold text-cream transition-all hover:bg-gold hover:text-obsidian"
                  >
                    Passer la commande
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </button>
                  <button
                    onClick={() => { setIsOpen(false); navigate("/canapes"); }}
                    className="w-full text-center text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-gold-dark"
                    data-testid="cart-continue-button"
                  >
                    Continuer mes achats
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
